import { Routes } from '@angular/router';
import { accountSetupGuard } from '@core/guards/account-setup.guard';
import { authGuard } from '@core/guards/auth.guard';
import { guestGuard } from '@core/guards/guest.guard';
import { ModalComponent } from '@features/settings/pages/modal/modal';
import { tuiGenerateDialogableRoute as tuiRouteDialog } from '@taiga-ui/kit';

const settingsDialogRoutes = [
  'settings/users-roles/users',
  'settings/users-roles/users/create',
  'settings/users-roles/users/:userId/edit',
  'settings/users-roles/roles',
  'settings/users-roles/roles/create',
  'settings/users-roles/roles/:roleId/edit',
  'settings/users-roles/roles/:roleId/copy',
  'settings/:section',
].map((path) =>
  tuiRouteDialog(ModalComponent, {
    path,
    outlet: 'modal',
    appearance: 'taiga settings',
  }),
);

export const routes: Routes = [
  // Authenticated
  {
    path: '',
    loadComponent: () => import('./layouts/main/main').then((m) => m.MainLayout),
    canActivate: [authGuard, accountSetupGuard],
    children: [
      {
        path: 'dashboard',
        loadChildren: () =>
          import('./features/dashboard/dashboard.routes').then((m) => m.dashboardRoutes),
        data: {
          breadcrumb: 'Home',
        },
      },
      {
        path: 'athletes',
        loadChildren: () =>
          import('./features/athletes/athletes.routes').then((m) => m.athletesRoutes),
        title: 'Athletes',
        data: {
          breadcrumb: 'Athletes',
        },
      },
      {
        path: 'performance-center',
        loadChildren: () =>
          import('./features/performance-center/performance-center.routes').then(
            (m) => m.performanceCenterRoutes,
          ),
        data: {
          breadcrumb: 'Tests',
        },
      },
      {
        path: 'test-creation',
        loadChildren: () =>
          import('./features/test-creation/test-creation.routes').then((m) => m.testCreationRoutes),
        data: {
          breadcrumb: 'Test Creation',
        },
      },
      {
        path: 'college',
        loadChildren: () =>
          import('./features/college/college.routes').then((m) => m.collegeRoutes),
      },
      {
        path: 'features',
        loadChildren: () =>
          import('./features/features/features.routes').then((m) => m.featuresRoutes),
      },
      {
        path: 'training-zone-builder',
        loadChildren: () =>
          import('./features/training-zone-builder/training-zone-builder.routes').then(
            (m) => m.trainingZoneBuilderRoutes,
          ),
        data: {
          breadcrumb: 'Training Zone Builder',
        },
      },
      ...settingsDialogRoutes,
      {
        path: '',
        redirectTo: 'dashboard',
        pathMatch: 'full',
      },
    ],
  },

  // Report generator — full-bleed, sibling to the main shell (no app chrome).
  // Siblings don't inherit the parent authGuard, so it's declared explicitly.
  {
    path: 'report-generator',
    loadComponent: () => import('./layouts/report/report-layout').then((m) => m.ReportLayout),
    canActivate: [authGuard],
    children: [
      {
        path: '',
        loadChildren: () =>
          import('./features/reporting/reporting.routes').then((m) => m.reportingRoutes),
      },
    ],
  },

  // Guests
  {
    path: 'auth',
    loadComponent: () => import('./layouts/full/full').then((m) => m.FullLayout),
    canActivate: [guestGuard],
    children: [
      {
        path: '',
        loadChildren: () => import('./features/auth/auth.routes').then((m) => m.authRoutes),
      },
    ],
  },

  // Legal notices — public, no auth required either way (accessible signed in or out).
  {
    path: 'legal',
    loadComponent: () => import('./layouts/full/full').then((m) => m.FullLayout),
    children: [
      {
        path: '',
        loadChildren: () => import('./features/legal/legal.routes').then((m) => m.legalRoutes),
      },
    ],
  },

  // Course purchase — public (shared purchase link + emailed magic link), signed in or out.
  {
    path: 'course-purchase',
    loadComponent: () => import('./layouts/full/full').then((m) => m.FullLayout),
    children: [
      {
        path: '',
        loadChildren: () =>
          import('./features/course-purchase/course-purchase.routes').then(
            (m) => m.coursePurchaseRoutes,
          ),
      },
    ],
  },

  // Plan purchase signup — public onboarding link generated by owners/agents in the old app.
  {
    path: 'signup',
    loadComponent: () => import('./layouts/full/full').then((m) => m.FullLayout),
    children: [
      {
        path: '',
        loadChildren: () => import('./features/signup/signup.routes').then((m) => m.signupRoutes),
      },
    ],
  },

  // Account setup — a new plan signup's company details, required before the app opens.
  {
    path: 'additional-info',
    loadComponent: () => import('./layouts/full/full').then((m) => m.FullLayout),
    canActivate: [authGuard],
    children: [
      {
        path: '',
        loadComponent: () =>
          import('./features/account-setup/pages/account-setup/account-setup').then(
            (m) => m.AccountSetupPage,
          ),
        title: 'Account Details',
      },
    ],
  },

  // Password recovery — public, no auth required either way (reached via emailed link).
  {
    path: 'forgot-password',
    loadComponent: () => import('./layouts/full/full').then((m) => m.FullLayout),
    children: [
      {
        path: '',
        loadComponent: () =>
          import('./features/auth/pages/forgot-password/forgot-password').then(
            (m) => m.ForgotPasswordPage,
          ),
        title: 'Forgot Password',
      },
    ],
  },
  {
    path: 'reset-password/:key',
    loadComponent: () => import('./layouts/full/full').then((m) => m.FullLayout),
    children: [
      {
        path: '',
        loadComponent: () =>
          import('./features/auth/pages/reset-password/reset-password').then(
            (m) => m.ResetPasswordPage,
          ),
        title: 'Reset Password',
      },
    ],
  },

  // Fallback
  {
    path: '**',
    loadComponent: () => import('./features/not-found/not-found').then((m) => m.NotFoundPage),
    title: 'Page Not Found',
  },
];
